import { FunctionComponent } from 'react';
import { useRouter } from 'next/router';
import classnames from 'classnames';

// Types
import { PathLevelProps } from '@Components/UI/Breadcrumb';

// Components
import Head from 'next/head';
import Tabs from '@Pages/bai-viet/danh-muc/Components/Tabs';
import PostsWithCategory from '@Pages/bai-viet/danh-muc/Components/PostsWithCategory';
import ResultPostLayout from '@Pages/bai-viet/Components/Layout/Components/ResultPost';

// Contexts
import { useLayoutContext } from '@Components/Layout/Contexts';
import { ViewMedia, useViewDetected } from '@Components/Utils/ViewDetected';
import { usePostsManager, withPostsManager } from '@Pages/bai-viet/Contexts/posts';

// Styles
import styles from './Style.module.scss';

// Icons
import { faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Types
import { NextSeoProps } from 'next-seo';
import { CategoryItem } from '@Components/Categories/Context';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';

// Utilties
import { openApp } from '@Libs/utils';
import { getServerSideHttp } from '@Libs/http/cms';

// Api
import postApi from '@Libs/api/cms/posts';
import PostType from '@Pages/bai-viet/[id]/PostDetail';
import postCategoriesApi from '@Libs/api/cms/postCategories';

const LIMIT = 9;

const paths = (category: CategoryItem, keyword?: string): Array<PathLevelProps> => {
    const path = [
        {
            label: 'Tra cứu',
            path: '/tra-cuu',
        },
        {
            label: 'Thư viện sức khỏe',
            path: '/bai-viet',
        },
    ];

    if (keyword) {
        path.push(
            ...[
                {
                    label: category.title,
                    path: `/bai-viet/danh-muc/${category.slug}`,
                },
                {
                    label: 'Kết quả tìm kiếm',
                    path: '',
                },
            ],
        );
    } else {
        path.push({
            label: category.title,
            path: '',
        });
    }

    return path;
};

const CategorizedPosts: FunctionComponent<InferGetServerSidePropsType<typeof getServerSideProps>> = () => {
    const router = useRouter();

    const { onScroll } = useLayoutContext();
    const { deviceType } = useViewDetected();

    const isMobile = ['mobile', 'tablet'].includes(deviceType);

    const { posts, hasMore, category, keyword, loadMore } = usePostsManager();

    openApp(`edoctor://article/${category.id}`);

    onScroll(
        (event, { isBottom }) => {
            if (['mobile', 'tablet'].includes(deviceType) && isBottom) {
                handleLoadMore();
            }
        },
        [posts],
    );

    const handleLoadMore = async () => loadMore();

    const handleSearch = (keyword: string) =>
        router.push({
            pathname: `/bai-viet/danh-muc/${category.slug}`,
            query: {
                s: keyword,
            },
        });

    const className = classnames([
        styles.wrapper,
        {
            [styles.mobile]: isMobile,
        },
    ]);

    return (
        <>
            <Head>
                <meta property="al:ios:url" content={`edoctor://article/${category?.id}`} />
                <meta property="al:android:url" content={`edoctor://article/${category?.id}`} />
            </Head>

            <ResultPostLayout
                keyword={keyword}
                className={className}
                title={category.title}
                onSearch={handleSearch}
                isEmpty={posts.length === 0}
                subTitle={category.description}
                paths={paths(category, keyword)}
                placeholder={`Tìm kiếm ${category.title} ngay...`}
            >
                <ViewMedia is={['mobile', 'tablet']}>
                    <div className={styles.header}>
                        <div className={styles.header_title}>{category.title}</div>
                        <div className={styles.header_description}>{category.description}</div>
                    </div>
                    <div className={styles.tab_category_wrapper}>
                        <Tabs categoryId={category.id} className={styles.tab_category} />
                    </div>
                </ViewMedia>

                <div className={styles.posts}>
                    <PostsWithCategory />

                    <ViewMedia is="desktop">
                        {hasMore && (
                            <span className={styles.view_more} onClick={handleLoadMore}>
                                <FontAwesomeIcon
                                    width={16}
                                    height={16}
                                    className={styles.icon_arrow}
                                    icon={faArrowDown}
                                />
                            </span>
                        )}
                    </ViewMedia>
                </div>
            </ResultPostLayout>
        </>
    );
};

export default withPostsManager(CategorizedPosts)(LIMIT);

export const getServerSideProps: GetServerSideProps = async (ctx) => {
    const http = getServerSideHttp(ctx);

    const keyword = ctx?.query?.s || '';
    const categoryId = ctx?.query?.id || '';

    const category: CategoryItem = await postCategoriesApi(http).findOne(categoryId.toString());
    if (!category) {
        return {
            notFound: true,
        };
    } else if (categoryId !== category.slug) {
        return {
            redirect: {
                statusCode: 301,
                destination: `/bai-viet/danh-muc/${category?.slug}`,
            },
        };
    }

    const posts: PostType[] = await postApi(http).find({
        ...(keyword && { _q: keyword.toString() }),
        category: category.id,
        _limit: LIMIT,
        _start: 0,
    });

    const seo: NextSeoProps = {
        title: `eDoctor: Danh mục bài viết ${category?.title}`,
    };

    return {
        props: {
            seo,
            posts,
            keyword,
            category,
        },
    };
};
